import type { TablesInsert, TablesUpdate } from '@/types/db';
import { Interview, InterviewVariant } from '@/types/pulsedb';
import { resolveInterviewVariant } from '@/utils/pulse/interviewVariants';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';
import { getInterview } from './interviews';

export const getInterviewVariants = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number
  ): Promise<InterviewVariant[]> => {
    const { data } = await supabase
      .from('interview_variants')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('deleted', false)
      .order('id', { ascending: true })
      .throwOnError();
    return (data ?? []) as InterviewVariant[];
  }
);

/** A variant by its id, for a conversation that recorded which one it ran. */
export const getInterviewVariant = cache(
  async (
    supabase: SupabaseClient,
    variantId: number
  ): Promise<InterviewVariant | null> => {
    const { data } = await supabase
      .from('interview_variants')
      .select('*')
      .eq('id', variantId)
      .maybeSingle()
      .throwOnError();
    return data as InterviewVariant | null;
  }
);

/**
 * A variant by its own slug. A variant carries its own link, so this is how a
 * respondent-facing slug is recognised as one before falling back to the
 * interview it belongs to.
 */
export const getInterviewVariantBySlug = cache(
  async (
    supabase: SupabaseClient,
    slug: string
  ): Promise<InterviewVariant | null> => {
    const { data } = await supabase
      .from('interview_variants')
      .select('*')
      .eq('slug', slug)
      .eq('deleted', false)
      .maybeSingle()
      .throwOnError();
    return data as InterviewVariant | null;
  }
);

/**
 * The interview a conversation actually ran under: the parent with its
 * variant's overrides laid over it, or the parent alone where it came through
 * the interview's own link.
 *
 * Anything deciding after the fact what a conversation was collected under —
 * what it pays, what cap it counts against, how it was taken — should read this
 * rather than `interviews` by `conversation.interview_id`, which answers what
 * the interview says today and not what the respondent was actually offered.
 *
 * Identity follows `resolveInterviewVariant`: `id` stays the interview's, and
 * `slug` becomes the variant's link.
 */
export const getResolvedInterviewForConversation = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<Interview | null> => {
    const { data: conversation } = await supabase
      .from('conversations')
      .select('interview_id, interview_variant_id')
      .eq('id', conversationId)
      .maybeSingle()
      .throwOnError();
    if (!conversation) return null;

    const interview = await getInterview(supabase, conversation.interview_id);
    if (!interview) return null;

    const variant = conversation.interview_variant_id
      ? await getInterviewVariant(supabase, conversation.interview_variant_id)
      : null;

    return resolveInterviewVariant(interview, variant);
  }
);

export async function insertInterviewVariant(
  supabase: SupabaseClient,
  variant: TablesInsert<'interview_variants'>
): Promise<InterviewVariant> {
  const { data } = await supabase
    .from('interview_variants')
    .insert(variant)
    .select()
    .single()
    .throwOnError();
  return data as InterviewVariant;
}

export async function updateInterviewVariant(
  supabase: SupabaseClient,
  interviewId: number,
  variantId: number,
  patch: TablesUpdate<'interview_variants'>
): Promise<InterviewVariant> {
  const { data } = await supabase
    .from('interview_variants')
    .update(patch)
    .eq('id', variantId)
    .eq('interview_id', interviewId)
    .select()
    .single()
    .throwOnError();
  return data as InterviewVariant;
}

/**
 * Soft-deletes a variant. Scoped to its interview so an id from elsewhere
 * cannot reach it. Conversations already recorded against the variant keep
 * pointing at it; only the author's list stops showing it.
 */
export async function deleteInterviewVariant(
  supabase: SupabaseClient,
  interviewId: number,
  variantId: number
) {
  await supabase
    .from('interview_variants')
    .update({ deleted: true })
    .eq('id', variantId)
    .eq('interview_id', interviewId)
    .throwOnError();
}
